import { ArrowRight, Sparkles } from "lucide-react";
import { useI18n } from "../i18n/i18n";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/d3759f07-bd3e-4f26-ba74-3dc302d64527/images/6195c283fc718ccc8e0ace1fa2d774328a99661d7fb3c2ef93bb729e0e8b1725.png";

export default function Hero() {
  const { t } = useI18n();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden cs-grain"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1e2d3d]/85 via-[#1e2d3d]/70 to-[#1e2d3d]" />
      <div className="absolute inset-0 cs-hex-grid opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-28 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 cs-fade-up">
          <div className="inline-flex items-center gap-2 cs-glass px-3 py-1.5 rounded-full mb-7">
            <Sparkles className="w-3.5 h-3.5 text-sky-400" />
            <span className="cs-overline" style={{ letterSpacing: "0.18em" }}>
              {t("hero.overline")}
            </span>
          </div>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-[72px] leading-[1.05] tracking-tight text-white max-w-4xl"
            data-testid="hero-title"
          >
            {t("hero.title_1")}{" "}
            <span className="text-sky-400">{t("hero.title_2")}</span>{" "}
            {t("hero.title_3")}
          </h1>

          <p className="mt-7 text-base sm:text-lg text-slate-100 max-w-2xl leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("contact")}
              className="cs-btn-primary"
              data-testid="hero-cta-primary"
            >
              {t("hero.cta_primary")}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("services")}
              className="cs-btn-secondary"
              data-testid="hero-cta-secondary"
            >
              {t("hero.cta_secondary")}
            </button>
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-4 lg:gap-5 cs-fade-up" style={{ animationDelay: "120ms" }}>
          {[
            { v: t("hero.stat_1_value"), l: t("hero.stat_1_label") },
            { v: t("hero.stat_2_value"), l: t("hero.stat_2_label") },
            { v: t("hero.stat_3_value"), l: t("hero.stat_3_label") },
          ].map((s, i) => (
            <div
              key={i}
              className="cs-glass rounded-xl px-5 py-5 lg:py-6"
              data-testid={`hero-stat-${i}`}
            >
              <p className="font-display text-3xl lg:text-4xl text-white leading-none">{s.v}</p>
              <p className="text-xs lg:text-sm text-slate-400 mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500">
        <span className="text-[10px] uppercase tracking-[0.3em]">scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-slate-500 to-transparent" />
      </div>
    </section>
  );
}
